import React from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  List,
  Row,
  Skeleton,
  Typography,
} from 'antd';
import type { AuthStatus } from '../../api/MarketplaceAuth';
import type { MpWarehouse } from '../../api/MpWarehouses';
import type { CatalogStats } from './useIntegrationDetail';

const { Text, Title } = Typography;

type AttentionItem = { key: string; text: string; tab?: string };

type Props = {
  data: AuthStatus;
  warehouses: MpWarehouse[];
  catalogStats: CatalogStats | null;
  lastSyncLabel?: string | null;
  attention: AttentionItem[];
  loading?: boolean;
  onGoTab: (tab: string) => void;
};

export const OverviewTab = ({
  data,
  warehouses,
  catalogStats,
  lastSyncLabel,
  attention,
  loading,
  onGoTab,
}: Props) => {
  const active = warehouses.filter((w) => w.is_active).length;
  const withErrors = warehouses.filter(
    (w) => w.status === 'archived' || w.status === 'error',
  ).length;
  const connected = Boolean(data.apiToken?.trim() || data.cookie?.trim());

  if (loading) {
    return <Skeleton active paragraph={{ rows: 6 }} />;
  }

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={14}>
        <Card
          bordered={false}
          style={{ borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}
        >
          <SpaceStatus connected={connected} lastSyncLabel={lastSyncLabel} />
          <Descriptions
            column={{ xs: 1, sm: 2 }}
            size="small"
            style={{ marginTop: 16 }}
          >
            <Descriptions.Item label="Seller ID">
              {data.sellerId || <Text type="secondary">Не заполнено</Text>}
            </Descriptions.Item>
            <Descriptions.Item label="Складов">
              {warehouses.length}
            </Descriptions.Item>
            <Descriptions.Item label="Активных">{active}</Descriptions.Item>
            <Descriptions.Item label="С ошибками / архивом">
              {withErrors}
            </Descriptions.Item>
            {catalogStats ? (
              <>
                <Descriptions.Item label="Товаров в каталоге">
                  {catalogStats.total}
                </Descriptions.Item>
                <Descriptions.Item label="Без связи с 1С">
                  {catalogStats.unlinked}
                </Descriptions.Item>
              </>
            ) : null}
          </Descriptions>
        </Card>
      </Col>
      <Col xs={24} lg={10}>
        <Card
          bordered={false}
          style={{ borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}
          title={
            <Title level={5} style={{ margin: 0 }}>
              Требуют внимания
            </Title>
          }
        >
          {attention.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Критичных проблем нет"
            />
          ) : (
            <List
              size="small"
              dataSource={attention}
              renderItem={(item) => (
                <List.Item
                  actions={
                    item.tab
                      ? [
                          <Button
                            key="go"
                            type="link"
                            size="small"
                            onClick={() => onGoTab(item.tab!)}
                          >
                            Исправить
                          </Button>,
                        ]
                      : undefined
                  }
                >
                  <Badge status="warning" text={item.text} />
                </List.Item>
              )}
            />
          )}
        </Card>
      </Col>
    </Row>
  );
};

const SpaceStatus = ({
  connected,
  lastSyncLabel,
}: {
  connected: boolean;
  lastSyncLabel?: string | null;
}) => (
  <div>
    <Badge
      status={connected ? 'success' : 'warning'}
      text={
        <Text strong style={{ fontSize: 16 }}>
          {connected ? 'Интеграция работает' : 'Интеграция требует настройки'}
        </Text>
      }
    />
    <div style={{ marginTop: 4 }}>
      <Text type="secondary">
        {lastSyncLabel
          ? `Последняя успешная синхронизация: ${lastSyncLabel}`
          : 'Синхронизация ещё не выполнялась'}
      </Text>
    </div>
  </div>
);
